import React, { useState, useEffect } from "react"
import { Box, Tabs, Tab, Popover } from "@mui/material"
import { useTheme } from "@mui/system"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import InfoTooltip from "components/InfoTooltip"
import TierManagement from "./TierManagement"
import CategoryManagement from "./CategoryManagement"
import RewardManagement from "./RewardManagement"
import RedemptionManagement from "./RedemptionManagement"
import AnalyticsDashboard from "./AnalyticsDashboard"
import ShoppingSpreeConfiguration from "./ShoppingSpreeConfiguration"
import { Col, Container, Row } from "react-bootstrap"
import FileDownloadIcon from "@mui/icons-material/FileDownload"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import AnalyticsDateRangeFilter from "pages/private/DVM_Console/GroupedAnalytics/component/AnalyticsDateRangeFilter"
import { useDispatch } from "react-redux"
import { appActions } from "redux/app/slice/app"
import { createBreadcrumbs } from "helpers/breadCrumbs"

const TAB_GUIDES = {
  tiers:
    "Create membership tiers (e.g. Silver, Platinum) and set how members unlock them — by coins, streak, or both. Every reward requires a minimum tier before a member can redeem it.",
  categories:
    "Group rewards into categories shown to members in the app. Only active categories are visible — deactivating a category hides its rewards.",
  rewards:
    "Create the rewards members redeem with coins. A reward is shown to a member only when it's Enabled, within its valid date range, the member meets its tier and coin cost, and — during a Shopping Spree — it has a quantity set for the active spree.",
  redemptions:
    "Every reward members have redeemed, with status (pending / completed) and fulfilment details. Use this to track and fulfil orders.",
  analytics:
    "Redemption trends, top rewards, and coin spend for the selected date range. Use the date filter and export to review performance.",
  spree:
    "Schedule time-boxed spree windows. During an active spree, each reward's quantity is set per spree and resets every window. Outside a spree, per-spree quantity limits don't apply."
}

const tabLabel = (text, guide) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    {text}
    <InfoTooltip title={guide} />
  </Box>
)

const Rewards = () => {
  const theme = useTheme()
  const [breadCrumbs, setBreadCrumbs] = useState()
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState(0)
  const [dateAnchorEl, setDateAnchorEl] = useState(null)
  const dateOpen = Boolean(dateAnchorEl)
  const [addTrigger, setAddTrigger] = useState(0)
  const [exportTrigger, setExportTrigger] = useState(0)
  const [isCompare, setIsCompare] = useState(false)

  const addButtonLabel =
    activeTab === 0
      ? null
      : activeTab === 1
      ? "Add New Category"
      : activeTab === 2
      ? "Add New Reward"
      : activeTab === 5
      ? null
      : ""
  useEffect(() => {
    document.title = "Rewards Configurations"
    dispatch(appActions.initFilters())
    setBreadCrumbs(createBreadcrumbs())
  }, [])

  return (
    <Box sx={{ pb: 4 }}>
      <Header title="Rewards Configurations" back={true} breadCrumbs={breadCrumbs}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {activeTab < 3 && activeTab !== 0 && activeTab !== 5 ? (
            <AppButton onClick={() => setAddTrigger((n) => n + 1)} sx={{ padding: "10px 20px" }}>
              {addButtonLabel}
            </AppButton>
          ) : activeTab === 4 && activeTab !== 0 ? (
            <>
              <AppButton
                sx={{ marginRight: "10px", padding: "10px 20px" }}
                onClick={(e) => setDateAnchorEl(e.currentTarget)}>
                <CalendarMonthIcon htmlColor={theme.palette.secondary.main} className="me-2" />
                Date Range
                <KeyboardArrowDownIcon className="ms-2" />
              </AppButton>
              <Popover
                open={dateOpen}
                anchorEl={dateAnchorEl}
                onClose={() => setDateAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                sx={{
                  "& .MuiPaper-root": {
                    backgroundColor: theme.palette.primary.main,
                    border: "1px solid #333",
                    borderRadius: "12px",
                    marginTop: "8px",
                    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.4)",
                    overflow: "visible"
                  }
                }}>
                <AnalyticsDateRangeFilter isCompare={isCompare} setIsCompare={setIsCompare} />
              </Popover>
              <AppButton
                onClick={() => setExportTrigger((n) => n + 1)}
                variant="outlined"
                sx={{
                  padding: "10px 20px"
                }}
                startIcon={<FileDownloadIcon />}>
                Export CSV
              </AppButton>
            </>
          ) : null}
        </Box>
      </Header>

      <Container fluid style={{ minWidth: "100%" }}>
        <Row>
          <Col lg="12" className="p-0">
            <Box sx={{ borderBottom: `1px solid ${theme.palette.secondary.main}30`, mb: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => {
                  setActiveTab(v)
                  setAddTrigger(0)
                }}
                sx={{
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontFamily: "Inter-Regular",
                    fontSize: "15px",
                    fontWeight: 500,
                    color: theme.palette.text.secondary,
                    "&.Mui-selected": { color: theme.palette.secondary.main }
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: theme.palette.secondary.main
                  }
                }}>
                <Tab label={tabLabel("Tier Management", TAB_GUIDES.tiers)} />
                <Tab label={tabLabel("Category Management", TAB_GUIDES.categories)} />
                <Tab label={tabLabel("Rewards", TAB_GUIDES.rewards)} />
                <Tab label={tabLabel("Redemptions", TAB_GUIDES.redemptions)} />
                <Tab label={tabLabel("Analytics", TAB_GUIDES.analytics)} />
                <Tab label={tabLabel("Shopping Spree", TAB_GUIDES.spree)} />
              </Tabs>
            </Box>

            <Box>
              {activeTab === 0 && <TierManagement addTrigger={addTrigger} />}
              {activeTab === 1 && <CategoryManagement addTrigger={addTrigger} />}
              {activeTab === 2 && <RewardManagement addTrigger={addTrigger} />}
              {activeTab === 3 && <RedemptionManagement />}
              {activeTab === 4 && <AnalyticsDashboard exportTrigger={exportTrigger} />}
              {activeTab === 5 && <ShoppingSpreeConfiguration />}
            </Box>
          </Col>
        </Row>
      </Container>
    </Box>
  )
}

export default Rewards
